import { CellValue } from './constants';
import type { Clue, PuzzleDefinition } from './types';

export type SolutionCorrections = {
  indices: number[];
  values: number[];
};

/**
 * Returns the indices and correct values for any filled cells that do not match the solution.
 * Unfilled cells are ignored.
 */
/** A cell set to something the solution doesn't have there (an unfilled cell is never wrong). */
export function isCellWrong(value: number, solutionValue: number): boolean {
  return value !== CellValue.UNFILLED && value !== solutionValue;
}

export function getSolutionCorrections(
  board: number[],
  solution: number[]
): SolutionCorrections {
  const indices: number[] = [];
  const values: number[] = [];
  const length = Math.min(board.length, solution.length);

  for (let i = 0; i < length; i++) {
    if (isCellWrong(board[i], solution[i])) {
      indices.push(i);
      values.push(solution[i]);
    }
  }

  return { indices, values };
}

/** The clue-bearing parts of a puzzle needed to check a board. */
export type ClueGrid = Pick<PuzzleDefinition, 'rows' | 'columns' | 'rowClues' | 'columnClues'>;

/**
 * Returns true when the filled cells of one line form exactly the clue runs.
 *
 * A run is a maximal stretch of one fill color. Unfilled and crossed cells both
 * count as blank, and adjacent cells of different colors are separate runs, so
 * no separator is needed between differently colored clues.
 */
export function lineSatisfiesClues(line: ArrayLike<number>, clues: readonly Clue[]): boolean {
  let clueIndex = 0;
  let runColor: number = CellValue.CROSSED;
  let runLength = 0;

  const closeRun = (): boolean => {
    if (runLength === 0) return true;
    const clue = clues[clueIndex];
    if (!clue || clue.colorIdx !== runColor || clue.amount !== runLength) return false;
    clueIndex++;
    runLength = 0;
    return true;
  };

  for (let i = 0; i < line.length; i++) {
    const value = line[i];
    const color = value > CellValue.CROSSED ? value : CellValue.CROSSED;
    if (color !== runColor) {
      if (!closeRun()) return false;
      runColor = color;
    }
    if (color !== CellValue.CROSSED) runLength++;
  }

  return closeRun() && clueIndex === clues.length;
}

/**
 * Returns true when every row and column of the board satisfies its clues.
 *
 * This is the solve rule for every puzzle: it accepts any valid answer, not just
 * the stored solution, and does not depend on a stored solution existing.
 */
export function boardSatisfiesClues(board: ArrayLike<number>, puzzle: ClueGrid): boolean {
  const { rows, columns } = puzzle;
  if (board.length !== rows * columns) return false;
  for (let row = 0; row < rows; row++) if (!checkLine(board, puzzle, 'rows', row)) return false;
  for (let column = 0; column < columns; column++) if (!checkLine(board, puzzle, 'columns', column)) return false;
  return true;
}

/**
 * Which lines of a board satisfy their clues, kept up to date move by move:
 * a move re-checks only the rows and columns it touched, instead of the
 * whole board (`boardSatisfiesClues`). Immutable: an update returns new
 * checks, sharing what didn't change.
 */
export interface LineChecks {
  rows: readonly boolean[];
  columns: readonly boolean[];
  /** How many lines don't satisfy their clues (0: solved). */
  unsatisfied: number;
}

/** Every line checked (a new board, or one loaded whole). */
export function checkAllLines(board: ArrayLike<number>, puzzle: ClueGrid): LineChecks {
  const { rows, columns } = puzzle;
  if (board.length !== rows * columns) return { rows: [], columns: [], unsatisfied: Math.max(1, rows + columns) };
  const rowChecks = Array.from({ length: rows }, (_, row) => checkLine(board, puzzle, 'rows', row));
  const columnChecks = Array.from({ length: columns }, (_, column) => checkLine(board, puzzle, 'columns', column));
  return { rows: rowChecks, columns: columnChecks, unsatisfied: countFalse(rowChecks) + countFalse(columnChecks) };
}

/** `checks` after the cells at `changed` changed (the board as it is now); the same checks when no line's result changed. */
export function updateLineChecks(checks: LineChecks, board: ArrayLike<number>, puzzle: ClueGrid, changed: Iterable<number>): LineChecks {
  const { rows, columns } = puzzle;
  if (checks.rows.length !== rows || checks.columns.length !== columns) return checkAllLines(board, puzzle);
  const touched: Record<Axis, Set<number>> = { rows: new Set(), columns: new Set() };
  for (const index of changed) {
    if (index < 0 || index >= rows * columns) continue;
    touched.rows.add(Math.floor(index / columns));
    touched.columns.add(index % columns);
  }
  let unsatisfied = checks.unsatisfied;
  const recheck = (axis: Axis): readonly boolean[] => {
    let next = checks[axis];
    for (const line of touched[axis]) {
      const ok = checkLine(board, puzzle, axis, line);
      if (ok === next[line]) continue;
      if (next === checks[axis]) next = [...next];
      (next as boolean[])[line] = ok;
      unsatisfied += ok ? -1 : 1;
    }
    return next;
  };
  const rowChecks = recheck('rows');
  const columnChecks = recheck('columns');
  return rowChecks === checks.rows && columnChecks === checks.columns
    ? checks
    : { rows: rowChecks, columns: columnChecks, unsatisfied };
}

type Axis = 'rows' | 'columns';

/** Whether one row or column of the board satisfies its clues. */
function checkLine(board: ArrayLike<number>, puzzle: ClueGrid, axis: Axis, index: number): boolean {
  const { rows, columns } = puzzle;
  const isRow = axis === 'rows';
  const length = isRow ? columns : rows;
  const start = isRow ? index * columns : index;
  const step = isRow ? 1 : columns;
  const line = new Array<number>(length);
  for (let i = 0; i < length; i++) line[i] = board[start + i * step];
  return lineSatisfiesClues(line, (isRow ? puzzle.rowClues : puzzle.columnClues)[index] ?? []);
}

function countFalse(values: readonly boolean[]): number {
  let count = 0;
  for (const value of values) if (!value) count++;
  return count;
}

/**
 * The clues for one line of a solution (board encoding: values above 0 are
 * colors; 0 and -1 are blank). Adjacent runs of different colors are
 * separate clues.
 */
export function lineClues(line: ArrayLike<number>): Clue[] {
  const clues: Clue[] = [];
  let runColor = 0;
  let runLength = 0;
  for (let i = 0; i <= line.length; i++) {
    const value = i < line.length ? line[i] : 0;
    const color = value > 0 ? value : 0;
    if (color === runColor && color !== 0) {
      runLength++;
      continue;
    }
    if (runColor !== 0) clues.push({ colorIdx: runColor, amount: runLength });
    runColor = color;
    runLength = color !== 0 ? 1 : 0;
  }
  return clues;
}

/** Row and column clues for a full solution. */
export function cluesFromSolution(
  solution: ArrayLike<number>,
  rows: number,
  columns: number,
): { rowClues: Clue[][]; columnClues: Clue[][] } {
  const rowClues: Clue[][] = [];
  const line: number[] = [];
  for (let row = 0; row < rows; row++) {
    line.length = 0;
    for (let column = 0; column < columns; column++) line.push(solution[row * columns + column]);
    rowClues.push(lineClues(line));
  }
  const columnClues: Clue[][] = [];
  for (let column = 0; column < columns; column++) {
    line.length = 0;
    for (let row = 0; row < rows; row++) line.push(solution[row * columns + column]);
    columnClues.push(lineClues(line));
  }
  return { rowClues, columnClues };
}

/**
 * The starting board: all unfilled, with any given cells revealed (an empty
 * given shows as crossed).
 */
export function initialBoard(puzzle: Pick<PuzzleDefinition, 'rows' | 'columns' | 'givens'>): number[] {
  const board = new Array<number>(puzzle.rows * puzzle.columns).fill(CellValue.UNFILLED);
  const givens = puzzle.givens;
  if (givens) {
    givens.indices.forEach((index, i) => {
      if (index >= 0 && index < board.length) board[index] = Math.max(CellValue.CROSSED, givens.values[i] ?? CellValue.CROSSED);
    });
  }
  return board;
}

/** The set of given (locked) cell indices, empty when there are none. */
export function givenIndexSet(puzzle: Pick<PuzzleDefinition, 'givens'>): Set<number> {
  return new Set(puzzle.givens?.indices ?? []);
}

/**
 * True when the revealed cells include every filled cell: the puzzle would
 * start solved, with nothing left to do.
 */
export function givensRevealWholePicture(puzzle: Pick<PuzzleDefinition, 'givens' | 'solution'>): boolean {
  const given = givenIndexSet(puzzle);
  if (given.size === 0) return false;
  return puzzle.solution.every((value, index) => value <= 0 || given.has(index));
}
